import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from "@mui/material";
import {
  PeopleOutline,
  Description,
  ShoppingCart,
  Key,
} from "@mui/icons-material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import TurkeyMap from "react-turkey-map";
import axios from "axios";
import config from "../../config/config.js";
import { useAuth } from "../login/AuthContext.jsx";
import Loading from "../../components/Loading/Loading.jsx";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const DashboardCard = ({ icon: Icon, title, value, color }) => (
  <Card
    sx={{
      height: "100%",
      borderRadius: "12px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      transition: "transform 0.2s",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
      },
    }}
  >
    <CardContent>
      <Box display="flex" alignItems="center">
        <Box
          sx={{
            backgroundColor: `${color}15`,
            borderRadius: "12px",
            p: 2,
            mr: 2,
          }}
        >
          <Icon sx={{ fontSize: 40, color }} />
        </Box>
        <Box>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontWeight: 500,
              mb: 1,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              color: "text.primary",
            }}
          >
            {value}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "top",
      labels: {
        usePointStyle: true,
        padding: 20,
        font: {
          size: 12,
        },
      },
    },
    title: {
      display: true,
      text: "Sipariş Verileri",
      font: {
        size: 16,
        weight: "bold",
      },
      padding: {
        bottom: 30,
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        drawBorder: false,
      },
      ticks: {
        padding: 10,
      },
    },
    x: {
      grid: {
        display: false,
      },
      ticks: {
        padding: 10,
      },
    },
  },
  elements: {
    line: {
      tension: 0.4,
    },
    point: {
      radius: 4,
      borderWidth: 2,
      backgroundColor: "white",
    },
  },
};

/** Sadece bitiş tarihi geçmiş lisanslar kırmızı */
function expiringLicenseRowStyle(license) {
  const expiredByStatus = license?.durum === "Süresi Bitti";
  const end = license?.bitisTarihi ? new Date(license.bitisTarihi) : null;
  const expiredByDate =
    end && !Number.isNaN(end.getTime()) && end.setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);
  const expired = expiredByStatus || expiredByDate;
  return {
    rowBg: expired ? "rgba(255, 0, 0, 0.1)" : "inherit",
    textColor: expired ? "#d32f2f" : "inherit",
  };
}

export const Home = () => {
  const [expiringLicenses, setExpiringLicenses] = useState([]);
  const [expiringGastoraLicenses, setExpiringGastoraLicenses] = useState([]);
  const [expiringTab, setExpiringTab] = useState("pozitif-ofis");
  const [aktiveLicense, setAktiveLicense] = useState({ aktifLisansSayisi: 0 });
  const [aktiveCustomers, setAktiveCustomers] = useState({
    aktifMusteriSayisi: 0,
  });
  const [lisansTalepleri, setLisansTalepleri] = useState({
    lisansTalepSayisi: 0,
  });
  const [orders, setOrders] = useState({ siparisSayisi: 0 });
  const [siparisIlBilgisi, setSiparisIlBilgisi] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBlinking, setIsBlinking] = useState(true);
  const { token, hasPermission } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const axiosConfig = {
        headers: { Authorization: `Bearer ${token}` },
      };
      try {
        setIsLoading(true);
        const [
          expLicenses,
          expGastoraLicenses,
          activeLic,
          activeCust,
          orderResp,
          licenseReq,
          chartResp,
        ] = await Promise.all([
          axios.get(
            `${config.baseURL}/musteriler/expiring-licenses`,
            axiosConfig
          ),
          axios.get(
            `${config.baseURL}/gastora-lisanslar/expiring`,
            axiosConfig
          ),
          axios.get(`${config.baseURL}/dashboard/aktif-lisanslar`, axiosConfig),
          axios.get(
            `${config.baseURL}/dashboard/aktif-musteriler`,
            axiosConfig
          ),
          axios.get(
            `${config.baseURL}/dashboard/onay-bekleyen-siparisler`,
            axiosConfig
          ),
          axios.get(
            `${config.baseURL}/dashboard/onay-bekleyen-lisans-talepleri`,
            axiosConfig
          ),
          axios.get(
            `${config.baseURL}/dashboard/siparis-aylik-grafik`,
            axiosConfig
          ),
        ]);

        // Sadece admin ise bu isteği de yap
        let sipIlBilg = null;
        if (hasPermission("admin")) {
          const response = await axios.get(
            `${config.baseURL}/dashboard/onay-bekleyen-siparis-listesi-iller`,
            axiosConfig
          );
          sipIlBilg = response;
        }
        if (expLicenses.data.success)
          setExpiringLicenses(expLicenses.data.data);
        if (expGastoraLicenses.data.success)
          setExpiringGastoraLicenses(expGastoraLicenses.data.data);
        if (activeLic.data.success) setAktiveLicense(activeLic.data.data);
        if (activeCust.data.success) setAktiveCustomers(activeCust.data.data);
        if (orderResp.data.success) setOrders(orderResp.data.data);
        if (licenseReq.data.success) setLisansTalepleri(licenseReq.data.data);

        if (chartResp.data.success) {
          const chartDataArray = chartResp.data.data[0]; // İlk array'i alıyoruz çünkü veriler burada

          setChartData({
            labels: chartDataArray.map((item) => `${item.Ay} - ${item.Yil}`),
            datasets: [
              {
                label: "Son 6 Aylık Sipariş Verileri",
                data: chartDataArray.map((item) => item.SiparisSayisi),
                borderColor: "rgba(75, 192, 192, 1)",
                backgroundColor: "rgba(75, 192, 192, 0.2)",
                borderWidth: 2,
                tension: 0.4,
              },
            ],
          });
          if (hasPermission("admin") && sipIlBilg.data.success) {
            setSiparisIlBilgisi(sipIlBilg.data.data);
          }
        }
      } catch (error) {
        console.error("Veri yüklenirken hata:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [token]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsBlinking((prev) => !prev);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Satış Verileri",
        data: [],
        borderColor: "rgba(75, 192, 192, 1)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        borderWidth: 2,
        tension: 0.4,
      },
    ],
  });

  const colorData = {};
  siparisIlBilgisi.forEach((item) => {
    let plaka = item.ilBilgisi.PlakaKodu.toString(); // string'e çevir
    if (plaka.length === 1) {
      plaka = "0" + plaka;
    }

    colorData[plaka] = isBlinking ? "#00a7b5" : "#d4c201";
  });

  const tooltipData = {};

  siparisIlBilgisi.forEach((item) => {
    // Plaka kodunu stringe çevirip, başına sıfır ekliyoruz (2 haneli olacak)
    const plaka = item.ilBilgisi.PlakaKodu.toString().padStart(2, "0");
    const ilAdi = item.ilBilgisi.IlAdi;

    if (!tooltipData[plaka]) {
      tooltipData[plaka] = { ilAdi: ilAdi, count: 1 };
    } else {
      tooltipData[plaka].count++;
    }
  });

  // Sonrasında istediğiniz formatta string yapabilirsiniz
  for (const plaka in tooltipData) {
    const info = tooltipData[plaka];
    tooltipData[plaka] = `Sipariş Sayısı: ${info.count}`;
  }

  const displayedExpiring =
    expiringTab === "gastora" ? expiringGastoraLicenses : expiringLicenses;
  const expiringIdLabel = expiringTab === "gastora" ? "Paket İsmi" : "Client ID";

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Loading />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          height: "calc(100vh - 100px)",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
            "&:hover": {
              background: "#555",
            },
          },
        }}
      >
        <Grid container spacing={4}>
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            onClick={() => navigate(`/musteriler`)}
          >
            <DashboardCard
              icon={PeopleOutline}
              title="Müşteri Sayısı"
              value={aktiveCustomers.aktifMusteriSayisi}
              color="#00A7B5"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DashboardCard
              icon={Description}
              title="Aktif Lisans"
              value={aktiveLicense.aktifLisansSayisi}
              color="#00C08B"
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            onClick={() => navigate(`/siparisler`)}
          >
            <DashboardCard
              icon={ShoppingCart}
              title="Bekleyen Siparişler"
              value={orders.siparisSayisi}
              color="#aa9b01"
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            onClick={() => navigate(`/lisanslar`)}
          >
            <DashboardCard
              icon={Key}
              title="Bekleyen Lisanslar"
              value={lisansTalepleri.lisansTalepSayisi}
              color="#1C355E"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                height: "460px",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                borderRadius: "12px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: "bold",
                  mb: 1,
                  flexShrink: 0,
                }}
              >
                Lisans Bitiş Tarihi Yaklaşan Firmalar
              </Typography>
              <Tabs
                value={expiringTab}
                onChange={(_, v) => setExpiringTab(v)}
                sx={{ mb: 1, minHeight: 36, flexShrink: 0, "& .MuiTab-root": { minHeight: 36, py: 0.5, textTransform: "none" } }}
              >
                <Tab label="Pozitif Ofis" value="pozitif-ofis" />
                <Tab label="Gastro" value="gastora" />
              </Tabs>
              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#888",
                    borderRadius: "3px",
                  },
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Firma Adı
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {expiringIdLabel}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Lisans Bitiş
                      </TableCell>
                      {/* <TableCell sx={{ fontWeight: 'bold' }}>Durum</TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedExpiring.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ fontStyle: "italic", color: "gray" }}
                        >
                          {expiringTab === "gastora"
                            ? "45 gün içinde Gastro lisans yenilemeniz bulunmamaktadır."
                            : "45 gün içerisinde lisans yenilemeniz bulunmamaktadır."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayedExpiring.map((license, index) => {
                        const rowStyle = expiringLicenseRowStyle(license);
                        return (
                        <TableRow
                          key={`${expiringTab}-${license.lisansId || index}`}
                          sx={{
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                            backgroundColor: rowStyle.rowBg,
                          }}
                        >
                          <TableCell
                            sx={{
                              cursor: "pointer",
                              color: rowStyle.textColor,
                            }}
                            onClick={() =>
                              navigate(
                                `/musteriler/${license.musteriId || license.musteriNo}`
                              )
                            }
                          >
                            {license.musteriAdi}
                          </TableCell>
                          <TableCell sx={{ color: rowStyle.textColor }}>
                            {expiringTab === "gastora"
                              ? license.paketAdi || "Gastro"
                              : license.clientId || license.lisansKey}
                          </TableCell>
                          <TableCell sx={{ color: rowStyle.textColor }}>
                            {new Date(license.bitisTarihi).toLocaleDateString(
                              "tr-TR"
                            )}
                          </TableCell>
                        </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                height: "460px",
                borderRadius: "12px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <Box sx={{ height: "100%", position: "relative" }}>
                <Line options={chartOptions} data={chartData} />
              </Box>
            </Paper>
          </Grid>
          <>
            {hasPermission("admin") && (
              <Grid item xs={12}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <TurkeyMap
                    showTooltip
                    hoverable={true}
                    colorData={colorData}
                    tooltipData={tooltipData}
                  />
                </Paper>
              </Grid>
            )}
          </>
        </Grid>
      </Box>
    </Container>
  );
};

export default Home;
